import React, { useState, useEffect } from 'react';
import { Link, useParams, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useAuth, API } from '../App';
import axios from 'axios';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../components/ui/dialog';
import { 
  Search, MapPin, Calendar, User, Building, Bookmark, BookmarkCheck,
  ChevronLeft, ChevronRight, X, Plus, ArrowLeft, Loader2, Layers, Sparkles
} from 'lucide-react';
import { toast } from 'sonner';
import Header from '../components/Header';
import Footer from '../components/Footer';
import Breadcrumb from '../components/Breadcrumb';

const PROJECT_TYPES = [
  { value: 'all', label: 'All Types' },
  { value: 'residential', label: 'Residential' },
  { value: 'hospitality', label: 'Hospitality' },
  { value: 'commercial', label: 'Commercial' },
  { value: 'retail', label: 'Retail' },
  { value: 'healthcare', label: 'Healthcare' },
  { value: 'educational', label: 'Educational' },
  { value: 'cultural', label: 'Cultural' },
  { value: 'outdoor', label: 'Outdoor' },
];

// Project List Page
const ProjectInspirationGallery = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const { t } = useTranslation();
  
  const [projects, setProjects] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filters, setFilters] = useState({
    search: '',
    project_type: 'all',
    location: ''
  });
  const [savedProjects, setSavedProjects] = useState(new Set());

  // Reset scroll on mount
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  useEffect(() => {
    fetchProjects();
    if (user) {
      fetchSavedProjects();
    }
  }, [filters, user]);

  const fetchProjects = async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (filters.search) params.append('search', filters.search);
      if (filters.project_type && filters.project_type !== 'all') {
        params.append('project_type', filters.project_type);
      }
      if (filters.location) params.append('location', filters.location);

      const response = await axios.get(`${API}/project-inspiration?${params.toString()}`);
      setProjects(response.data.projects || []);
    } catch (error) {
      console.error('Failed to fetch projects:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchSavedProjects = async () => {
    try {
      const response = await axios.get(`${API}/project-inspiration/user/saved`, { withCredentials: true });
      setSavedProjects(new Set(response.data.map(p => p.project_id)));
    } catch (error) {
      console.error('Failed to fetch saved projects:', error);
    }
  };

  const toggleSave = async (projectId) => {
    if (!user) {
      toast.error('Please login to save projects');
      return;
    }

    try {
      await axios.post(`${API}/project-inspiration/${projectId}/save`, {}, { withCredentials: true });
      setSavedProjects(prev => {
        const newSet = new Set(prev);
        if (newSet.has(projectId)) {
          newSet.delete(projectId);
          toast.success('Removed from saved');
        } else {
          newSet.add(projectId);
          toast.success('Project saved!');
        }
        return newSet;
      });
    } catch (error) {
      toast.error('Failed to save project');
    }
  };

  // Translated project types
  const PROJECT_TYPES_TRANSLATED = [
    { value: 'all', label: t('inspiration.allTypes') },
    { value: 'residential', label: t('inspiration.residential') },
    { value: 'hospitality', label: t('inspiration.hospitality') },
    { value: 'commercial', label: t('inspiration.commercial') },
    { value: 'retail', label: t('inspiration.retail') },
    { value: 'healthcare', label: t('inspiration.healthcare') },
    { value: 'educational', label: t('inspiration.educational') },
    { value: 'cultural', label: t('inspiration.cultural') },
    { value: 'outdoor', label: t('inspiration.outdoor') },
  ];

  return (
    <div className="min-h-screen bg-background flex flex-col">
      <Header />
      
      <main className="flex-1 max-w-[1800px] mx-auto px-6 md:px-12 py-12 pt-28 w-full">
        {/* Breadcrumb */}
        <Breadcrumb currentPage={t('nav.inspiration')} />
        
        {/* Header */}
        <div className="mb-12">
          <p className="caption-text text-muted-foreground mb-2">{t('inspiration.title')}</p>
          <h1 className="heading-display text-4xl md:text-5xl font-medium">
            {t('inspiration.pageTitle')}
          </h1>
          <p className="text-muted-foreground mt-2 max-w-2xl">
            {t('inspiration.pageSubtitle')}
          </p>
        </div>

        {/* Filters */}
        <div className="flex flex-col md:flex-row gap-4 mb-8">
          <div className="input-icon-wrapper flex-1 max-w-md">
            <Search className="input-icon" />
            <Input
              placeholder={t('inspiration.searchProjects')}
              value={filters.search}
              onChange={(e) => setFilters(f => ({ ...f, search: e.target.value }))}
              className="input-field with-icon-left"
            />
          </div>
          
          <Select 
            value={filters.project_type} 
            onValueChange={(v) => setFilters(f => ({ ...f, project_type: v }))}
          >
            <SelectTrigger className="w-48 h-10">
              <SelectValue placeholder={t('inspiration.projectType')} />
            </SelectTrigger>
            <SelectContent>
              {PROJECT_TYPES_TRANSLATED.map(type => (
                <SelectItem key={type.value} value={type.value}>{type.label}</SelectItem>
              ))}
            </SelectContent>
          </Select>

          <div className="input-icon-wrapper max-w-xs">
            <MapPin className="input-icon" />
            <Input
              placeholder={t('inspiration.location')}
              value={filters.location}
              onChange={(e) => setFilters(f => ({ ...f, location: e.target.value }))}
              className="input-field with-icon-left"
            />
          </div>
        </div>

        {/* Projects Grid */}
        {loading ? (
          <div className="flex justify-center py-24">
            <Loader2 className="w-8 h-8 animate-spin text-muted-foreground" />
          </div>
        ) : projects.length === 0 ? (
          <div className="text-center py-24">
            <p className="text-muted-foreground">{t('inspiration.noProjectsFound')}</p>
            <p className="text-sm text-muted-foreground mt-2">
              {t('inspiration.checkBackSoon')}
            </p>
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {projects.map(project => (
              <div 
                key={project.project_id}
                className="group cursor-pointer"
                onClick={() => navigate(`/project-inspiration/${project.project_id}`)}
              >
                {/* Image */}
                <div className="relative aspect-[4/3] overflow-hidden bg-secondary mb-4">
                  {project.images?.[0] ? (
                    <img 
                      src={project.images[0]}
                      alt={project.title}
                      className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                    />
                  ) : (
                    <div className="w-full h-full flex items-center justify-center text-muted-foreground">
                      <Building className="w-12 h-12" />
                    </div>
                  )}
                  
                  {/* Save Button */}
                  <button
                    onClick={(e) => {
                      e.stopPropagation();
                      toggleSave(project.project_id);
                    }}
                    className="absolute top-4 right-4 w-10 h-10 bg-white/90 hover:bg-white flex items-center justify-center transition-colors"
                  >
                    {savedProjects.has(project.project_id) ? (
                      <BookmarkCheck className="w-5 h-5 text-primary" />
                    ) : (
                      <Bookmark className="w-5 h-5" />
                    )}
                  </button>

                  {/* Hotspot indicator */}
                  {project.material_hotspots?.length > 0 && (
                    <div className="absolute bottom-4 left-4 px-3 py-1 bg-black/70 text-white text-xs">
                      {project.material_hotspots.length} material{project.material_hotspots.length > 1 ? 's' : ''} tagged
                    </div>
                  )}
                </div>

                {/* Info */}
                <div>
                  <p className="text-xs text-muted-foreground uppercase tracking-wide mb-1">
                    {project.project_type?.replace('_', ' ')}
                  </p>
                  <h3 className="text-lg font-medium mb-1">{project.title}</h3>
                  <div className="flex items-center gap-4 text-sm text-muted-foreground">
                    {project.location && (
                      <span className="flex items-center gap-1">
                        <MapPin className="w-3 h-3" /> {project.location}
                      </span>
                    )}
                    {project.year_completed && (
                      <span className="flex items-center gap-1">
                        <Calendar className="w-3 h-3" /> {project.year_completed}
                      </span>
                    )}
                  </div>
                  {project.architect_name && (
                    <p className="text-sm text-muted-foreground mt-1">
                      By {project.architect_name}
                      {project.architect_firm && `, ${project.architect_firm}`}
                    </p>
                  )}
                </div>
              </div>
            ))}
          </div>
        )}
      </main>

      <Footer />
    </div>
  );
};

// Single Project Detail Page
export const ProjectInspirationDetail = () => {
  const { projectId } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  
  const [project, setProject] = useState(null);
  const [loading, setLoading] = useState(true);
  const [currentImageIndex, setCurrentImageIndex] = useState(0);
  const [selectedHotspot, setSelectedHotspot] = useState(null);
  const [isSaved, setIsSaved] = useState(false);
  const [shopTheLookLoading, setShopTheLookLoading] = useState(false);

  useEffect(() => {
    const fetchProject = async () => {
      try {
        const response = await axios.get(`${API}/project-inspiration/${projectId}`);
        setProject(response.data);
      } catch (error) {
        console.error('Failed to fetch project:', error);
        toast.error('Project not found');
      } finally {
        setLoading(false);
      }
    };

    fetchProject();
  }, [projectId]);

  const handleShopTheLook = async () => {
    if (!user) {
      toast.error('Please sign in to use this feature');
      navigate('/login', { state: { from: `/project-inspiration/${projectId}` } });
      return;
    }

    setShopTheLookLoading(true);
    try {
      const response = await axios.post(
        `${API}/project-inspiration/${projectId}/shop-the-look`,
        { board_name: `Inspired by: ${project?.title}` },
        { withCredentials: true }
      );
      
      toast.success(`Added ${response.data.added_count} materials to your board!`);
      
      // Navigate to the board
      setTimeout(() => {
        navigate(`/projects/${response.data.board_id}`);
      }, 1500);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to create board');
    } finally {
      setShopTheLookLoading(false);
    }
  };

  const nextImage = () => {
    if (project?.images?.length > 1) {
      setCurrentImageIndex((prev) => (prev + 1) % project.images.length);
    }
  };

  const prevImage = () => {
    if (project?.images?.length > 1) {
      setCurrentImageIndex((prev) => (prev - 1 + project.images.length) % project.images.length);
    }
  };

  const currentImageHotspots = project?.material_hotspots?.filter(
    h => h.image_index === currentImageIndex
  ) || [];

  if (loading) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <Loader2 className="w-8 h-8 animate-spin text-muted-foreground" />
      </div>
    );
  }

  if (!project) {
    return (
      <div className="min-h-screen bg-background">
        <Header />
        <main className="max-w-4xl mx-auto px-6 py-12 pt-28 text-center">
          <h1 className="text-2xl font-medium mb-4">Project Not Found</h1>
          <Link to="/project-inspiration">
            <Button variant="outline">
              <ArrowLeft className="w-4 h-4 mr-2" /> Back to Projects
            </Button>
          </Link>
        </main>
        <Footer />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background flex flex-col">
      <Header />
      
      <main className="flex-1 max-w-[1600px] mx-auto px-6 py-12 pt-28 w-full">
        {/* Back */}
        <div className="flex items-center justify-between mb-8">
          <Link 
            to="/project-inspiration"
            className="inline-flex items-center text-muted-foreground hover:text-foreground"
          >
            <ArrowLeft className="w-4 h-4 mr-2" /> Back to Projects
          </Link>
          
          {user?.is_admin && (
            <Link to={`/project-inspiration/${projectId}/edit-hotspots`}>
              <Button variant="outline" size="sm" className="gap-2">
                <Plus className="w-4 h-4" /> Edit Hotspots
              </Button>
            </Link>
          )}
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-12">
          {/* Image Gallery */}
          <div className="lg:col-span-2">
            <div className="relative aspect-[4/3] bg-secondary overflow-hidden">
              {project.images?.[currentImageIndex] && (
                <img 
                  src={project.images[currentImageIndex]}
                  alt={`${project.title} - Image ${currentImageIndex + 1}`}
                  className="w-full h-full object-cover"
                />
              )}

              {/* Material Hotspots */}
              {currentImageHotspots.map((hotspot, idx) => (
                <button
                  key={hotspot.hotspot_id || idx}
                  className="absolute w-8 h-8 bg-white/90 hover:bg-white border-2 border-primary rounded-full flex items-center justify-center transform -translate-x-1/2 -translate-y-1/2 transition-all hover:scale-110 shadow-lg"
                  style={{ left: `${hotspot.x_percent}%`, top: `${hotspot.y_percent}%` }}
                  onClick={() => setSelectedHotspot(hotspot)}
                >
                  <Plus className="w-4 h-4 text-primary" />
                </button>
              ))}

              {/* Navigation */}
              {project.images?.length > 1 && (
                <>
                  <button
                    onClick={prevImage}
                    className="absolute left-4 top-1/2 -translate-y-1/2 w-10 h-10 bg-white/90 hover:bg-white flex items-center justify-center"
                  >
                    <ChevronLeft className="w-5 h-5" />
                  </button>
                  <button
                    onClick={nextImage}
                    className="absolute right-4 top-1/2 -translate-y-1/2 w-10 h-10 bg-white/90 hover:bg-white flex items-center justify-center"
                  >
                    <ChevronRight className="w-5 h-5" />
                  </button>
                  <div className="absolute bottom-4 left-1/2 -translate-x-1/2 px-3 py-1 bg-black/70 text-white text-sm">
                    {currentImageIndex + 1} / {project.images.length}
                  </div>
                </>
              )}
            </div>

            {/* Thumbnail Strip */}
            {project.images?.length > 1 && (
              <div className="flex gap-2 mt-4 overflow-x-auto pb-2">
                {project.images.map((img, idx) => (
                  <button
                    key={idx}
                    onClick={() => setCurrentImageIndex(idx)}
                    className={`w-20 h-20 flex-shrink-0 ${idx === currentImageIndex ? 'ring-2 ring-primary' : 'opacity-60 hover:opacity-100'}`}
                  >
                    <img src={img} alt="" className="w-full h-full object-cover" />
                  </button>
                ))}
              </div>
            )}
          </div>

          {/* Project Info */}
          <div>
            <p className="text-xs text-muted-foreground uppercase tracking-wide mb-2">
              {project.project_type?.replace('_', ' ')}
            </p>
            <h1 className="heading-display text-3xl font-medium mb-4">{project.title}</h1>
            
            <div className="space-y-3 text-sm mb-6">
              {project.location && (
                <p className="flex items-center gap-2">
                  <MapPin className="w-4 h-4 text-muted-foreground" /> {project.location}
                </p>
              )}
              {project.year_completed && (
                <p className="flex items-center gap-2">
                  <Calendar className="w-4 h-4 text-muted-foreground" /> {project.year_completed}
                </p>
              )}
              {project.architect_name && (
                <p className="flex items-center gap-2">
                  <User className="w-4 h-4 text-muted-foreground" /> 
                  {project.architect_name}
                  {project.architect_firm && <span className="text-muted-foreground">• {project.architect_firm}</span>}
                </p>
              )}
            </div>

            {project.description && (
              <p className="text-muted-foreground mb-6">{project.description}</p>
            )}

            {/* Tagged Materials */}
            {project.material_hotspots?.length > 0 && (
              <div>
                <div className="flex items-center justify-between mb-3">
                  <h3 className="font-medium">Materials Used ({project.material_hotspots.length})</h3>
                  {user && (
                    <Button 
                      onClick={handleShopTheLook}
                      disabled={shopTheLookLoading}
                      className="bg-stone-900 hover:bg-stone-800 text-white gap-2"
                      data-testid="shop-the-look-btn"
                    >
                      {shopTheLookLoading ? (
                        <Loader2 className="w-4 h-4 animate-spin" />
                      ) : (
                        <Layers className="w-4 h-4" />
                      )}
                      Use the Look
                    </Button>
                  )}
                </div>
                {!user && (
                  <div className="bg-stone-50 border border-stone-200 p-4 mb-4 flex items-center justify-between">
                    <div className="flex items-center gap-2">
                      <Sparkles className="w-5 h-5 text-stone-500" />
                      <span className="text-sm text-stone-600">Sign in to save all materials to a board</span>
                    </div>
                    <Link to="/login">
                      <Button variant="outline" size="sm">Sign In</Button>
                    </Link>
                  </div>
                )}
                <div className="space-y-2">
                  {[...new Map(project.material_hotspots.map(h => [h.material_id, h])).values()].map((hotspot, idx) => (
                    <Link
                      key={idx}
                      to={`/materials/${hotspot.material_id}`}
                      className="flex items-center gap-3 p-3 bg-secondary/50 hover:bg-secondary transition-colors"
                    >
                      {hotspot.material_thumbnail && (
                        <img 
                          src={hotspot.material_thumbnail}
                          alt=""
                          className="w-12 h-12 object-cover"
                        />
                      )}
                      <div>
                        <p className="font-medium">{hotspot.material_name || hotspot.label}</p>
                        <p className="text-xs text-muted-foreground">{hotspot.material_type}</p>
                      </div>
                    </Link>
                  ))}
                </div>
              </div>
            )}
          </div>
        </div>
      </main>

      {/* Hotspot Detail Dialog */}
      <Dialog open={!!selectedHotspot} onOpenChange={() => setSelectedHotspot(null)}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>{selectedHotspot?.material_name || selectedHotspot?.label}</DialogTitle>
          </DialogHeader>
          {selectedHotspot && (
            <div className="space-y-4">
              {selectedHotspot.material_thumbnail && (
                <img 
                  src={selectedHotspot.material_thumbnail}
                  alt=""
                  className="w-full h-48 object-cover"
                />
              )}
              <p className="text-sm text-muted-foreground">
                Type: {selectedHotspot.material_type}
              </p>
              <div className="flex gap-2">
                <Link to={`/materials/${selectedHotspot.material_id}`} className="flex-1">
                  <Button className="w-full">View Material</Button>
                </Link>
                <Link to={`/materials/${selectedHotspot.material_id}/specification`} className="flex-1">
                  <Button variant="outline" className="w-full">Specification</Button>
                </Link>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>

      <Footer />
    </div>
  );
};

export default ProjectInspirationGallery;
